import { FetchResult, MutationResult, useMutation } from '@apollo/client';
import {
	CampaignObjective,
	CreateAdInput,
	CreateRedditAdsDocument,
	CreateRedditAdsMutation,
	CreateRedditCreativesDocument,
	CreateRedditCreativesMutation,
	CreativeContentInput,
	CreativeType, GroupTargetingDevicesOs,
} from 'wb-reddit/graphql/generated';
import { RedditCreateData } from 'wb-reddit/app/routes/superGrid/mutator/utils';

export function getAdClickUrl(ad: RedditCreateData): CreateAdInput['adClickUrl'] {
	if (ad?.parent?.parent?.campaignObjective !== CampaignObjective.AppInstalls) {
		return undefined;
	}

	if (ad?.parent?.deviceType?.[0]?.value === GroupTargetingDevicesOs.Android) {
		return `https://play.google.com/store/apps/details?id=${ad?.parent?.groupAppId}`;
	}

	if (ad?.parent?.deviceType?.[0]?.value === GroupTargetingDevicesOs.Ios) {
		return `https://apps.apple.com/bg/app/id${ad?.parent?.groupAppId}`;
	}

	return undefined;
}

function getCreativeContent(ad: RedditCreateData):CreativeContentInput[] | undefined {
	const destinationUrl = getAdClickUrl(ad) ?? ad.creativeContentDestinationUrl0 ?? '';

	switch (ad.creativeType) {
		case CreativeType.Image:
		case CreativeType.Video:
			return [{
				creativeContentCallToAction:   ad.creativeContentCallToAction0,
				creativeContentMediaUrl:       ad.creativeContentMediaUrl0?.mediaUrl ?? '',
				creativeContentDestinationUrl: destinationUrl,
				creativeContentDisplayUrl:     ad.creativeContentDisplayUrl0 ?? destinationUrl ?? '',
				creativeContentCaption:        ad.creativeContentCaption0 ?? '',
			}];
		case CreativeType.Carousel:
			return [...Array(6).keys()]
				.filter(index => !!ad[`creativeContentMediaUrl${index}`])
				.map((index) => {
					const carouselDestinationUrl = getAdClickUrl(ad)
                    ?? ad[`creativeContentDestinationUrl${index}`] ?? '';

					const carouselDisplayUrl = ad[`creativeContentDisplayUrl${index}`]?.length > 0
						? ad[`creativeContentDisplayUrl${index}`] : carouselDestinationUrl ?? '';

					return {
						creativeContentCallToAction:   ad[`creativeContentCallToAction${index}`],
						creativeContentMediaUrl:       ad[`creativeContentMediaUrl${index}`]?.mediaUrl ?? '',
						creativeContentDestinationUrl: carouselDestinationUrl,
						creativeContentDisplayUrl:     carouselDisplayUrl,
						creativeContentCaption:        ad[`creativeContentCaption${index}`] ?? '',
					};
				});
		case CreativeType.Text:
		default:
			return undefined;
	}
}

const useCreateRedditAds = (): [
	(adsInCreation: RedditCreateData[]) => Promise<FetchResult<CreateRedditAdsMutation>>,
	MutationResult<CreateRedditAdsMutation | CreateRedditCreativesMutation>,
] => {
	const [createRedditCreatives, creativesResult] = useMutation(CreateRedditCreativesDocument, {
		errorPolicy: 'all',
	});

	const [createRedditAds, adsResult] = useMutation(CreateRedditAdsDocument, {
		errorPolicy: 'all',
	});

	const onCreateRedditAds = async (
		adsInCreation: RedditCreateData[],
	) => {
		const { data: creativeData, errors: creativeErrors } = await createRedditCreatives({
			variables: {
				creatives: adsInCreation.map((ad) => ({
					creativeHash:          ad.hash,
					creativeType:          ad.creativeType,
					creativeHeadline:      ad.creativeHeadline ?? '',
					creativeText:          ad.creativeText === '' ? undefined : (ad.creativeText ?? undefined),
					profileId:             ad?.profile?.value ?? '',
					creativeContent:       getCreativeContent(ad),
					creativeThumbnailUrl:  ad.creativeThumbnailUrl,
					creativeAllowComments: ad.creativeAllowComments ?? false,
				})),
			},
		});

		const ads: CreateAdInput[] = adsInCreation.map((ad) => ({
			adAccountId:     ad.parent?.parent?.parent?.adAccount?.adAccountId,
			adEventTrackers: [
				...(ad.adClickEventTrackers ?? []),
				...(ad.adViewEventTrackers ?? []),
			] as CreateAdInput['adEventTrackers'],
			groupId:    ad.parent?.id,
			creativeId: creativeData?.createCreatives
				?.find(creative => creative?.creativeHash === ad.hash)?.creativeId,
			adHash:     ad.hash,
			adName:     ad.name,
			adStatus:   ad.adStatus,
			adClickUrl: getAdClickUrl(ad),
		}) as CreateAdInput).filter((ad): ad is CreateAdInput => !!ad.creativeId);

		const { data: adData, errors: adErrors, ...adResultRest } = await createRedditAds({
			variables: {
				ads,
			},
		});

		return {
			...adResultRest,
			data:   adData,
			errors: [...(adErrors ?? []), ...(creativeErrors ?? [])],
		};
	};

	return [
		onCreateRedditAds,
		{
			...adsResult,
			loading: adsResult.loading || creativesResult.loading,
			error:   adsResult.error || creativesResult.error,
			data:    adsResult.data || creativesResult.data,
		},
	];
};

export default useCreateRedditAds;
